import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import logo from "@/assets/turboclaim-logo.png";
import bgImage from "@/assets/login-background.jpeg";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
// import { CreateUserRequest } from "@/api/generated/client/models"; // No longer needed here as check is in schemas/auth.ts
import { signupSchema, SignupForm } from "@/schemas/auth";

export default function Signup() {
  const [, setLocation] = useLocation();
  const { user, isLoading: authLoading, signupMutation } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { toast } = useToast();

  // 3. Initialize Hook Form
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<SignupForm>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: ""
    }
  });

  // Prefill email from a claim invite link (?email=...) purely as a convenience so the
  // invitee doesn't have to retype it. Access is granted on signup via the invite TOKEN
  // (forwarded to the backend), which is the source of truth — so the claim is still
  // auto-granted even if the user edits this email before submitting. The email match is
  // only a secondary fallback for resolving any other invites sent to the same address.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const invitedEmail = params.get("email");
    if (invitedEmail) {
      setValue("email", invitedEmail);
    }
  }, [setValue]);

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && user) {
      // Check for pending invite token
      const pendingToken = sessionStorage.getItem("pendingInviteToken");
      if (pendingToken) {
        // Redirect back to accept invitation page
        setLocation(`/accept-invitation/${pendingToken}`);
      } else {
        setLocation("/");
      }
    }
  }, [user, authLoading, setLocation]);

  const onSubmit = (data: SignupForm) => {
    // Carry the claim invite token (if the user arrived from a claim invite link) so the
    // backend can grant access to the shared claim even if they change the email here.
    // The token is delivered in the URL FRAGMENT (#claimInvite=...) rather than the query
    // string so it is never sent to the server, logged by proxies/CDNs, or leaked via the
    // Referer header. Read it client-side from the hash.
    const claimInviteToken = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("claimInvite") ?? undefined;
    signupMutation.mutate(
      {
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        claimInviteToken,
      },
      {
        onSuccess: () => {
          // Check for pending invite token
          const pendingToken = sessionStorage.getItem("pendingInviteToken");

          if (pendingToken) {
            toast({
              title: "Account created",
              description: "Processing your invitation...",
            });
            // Will redirect via useEffect above
          } else {
            toast({
              title: "Account created",
              description: "Redirecting to subscription setup...",
            });
            setLocation("/plans");
          }
        },
        onError: (error) => {
          toast({
            title: "Signup failed",
            description: error instanceof Error ? error.message : "Failed to create account",
            variant: "destructive",
          });
        },
      }
    );
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-pulse">
            <img src={logo} alt="TurboClaim" className="h-12 mx-auto mb-4 opacity-60" />
          </div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] md:min-h-screen relative flex items-center justify-center">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${bgImage})`,
          backgroundPosition: "center bottom",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#1e3a5f]/40 via-transparent to-transparent" />
      </div>

      <div className="relative z-10 w-full max-w-md mx-4 max-sm:my-8">
        <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
          <div className="text-center mb-6">
            <img src={logo} alt="TurboClaim" className="h-10 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900">
              Create Your Account
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Sign up to get started with TurboClaim
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-4">
              <Label htmlFor="firstName" className="text-gray-700 font-medium text-sm">
                First Name
              </Label>
              <Input
                id="firstName"
                type="text"
                placeholder="Enter your first name"
                disabled={signupMutation.isPending}
                className={`mt-1.5 h-10 bg-gray-50/50 border-gray-300 focus:bg-white text-gray-900 ${errors.firstName ? 'border-red-500' : ''}`}
                {...register("firstName")}
              />
              {errors.firstName && (
                <p className="text-red-500 text-xs mt-1">{errors.firstName.message}</p>
              )}
            </div>

            <div className="mb-4">
              <Label htmlFor="lastName" className="text-gray-700 font-medium text-sm">
                Last Name
              </Label>
              <Input
                id="lastName"
                type="text"
                placeholder="Enter your last name"
                disabled={signupMutation.isPending}
                className={`mt-1.5 h-10 bg-gray-50/50 border-gray-300 focus:bg-white text-gray-900 ${errors.lastName ? 'border-red-500' : ''}`}
                {...register("lastName")}
              />
              {errors.lastName && (
                <p className="text-red-500 text-xs mt-1">{errors.lastName.message}</p>
              )}
            </div>

            <div className="mb-4">
              <Label htmlFor="email" className="text-gray-700 font-medium text-sm">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                disabled={signupMutation.isPending}
                className={`mt-1.5 h-10 bg-gray-50/50 border-gray-300 focus:bg-white text-gray-900 ${errors.email ? 'border-red-500' : ''}`}
                {...register("email")}
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
              )}
            </div>

            <div className="mb-4">
              <Label htmlFor="password" className="text-gray-700 font-medium text-sm">
                Password
              </Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a password"
                  disabled={signupMutation.isPending}
                  className={`h-10 bg-gray-50/50 border-gray-300 focus:bg-white text-gray-900 pr-10 ${errors.password ? 'border-red-500' : ''}`}
                  {...register("password")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>
              )}
            </div>

            <div className="mb-6">
              <Label htmlFor="confirmPassword" className="text-gray-700 font-medium text-sm">
                Confirm Password
              </Label>
              <div className="relative mt-1.5">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your password"
                  disabled={signupMutation.isPending}
                  className={`h-10 bg-gray-50/50 border-gray-300 focus:bg-white text-gray-900 pr-10 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                  {...register("confirmPassword")}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-red-500 text-xs mt-1">{errors.confirmPassword.message}</p>
              )}
            </div>

            <Button
              type="submit"
              size="lg"
              className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg"
              disabled={signupMutation.isPending}
            >
              {signupMutation.isPending ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Creating account...
                </>
              ) : (
                "Sign up"
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <span className="text-sm text-gray-600">Already have an account? </span>
            <a
              href="/login"
              className="text-sm text-blue-600 hover:text-blue-700 hover:underline font-medium"
              onClick={(e) => {
                e.preventDefault();
                setLocation("/login");
              }}
            >
              Login
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
